import { Pill, Text } from '@mantine/core';
import { Calendar, Clock, MapPin, User } from 'lucide-react';
import { FC } from 'react';

import Card from '@/components/Card.tsx';
import Heading4 from '@/components/text/Heading4.tsx';

import { urgency_priority_map } from '@/constants/urgency.ts';

import { EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    task: EnrichedTask;
    onClick: (task: EnrichedTask) => void;
    showLocationAndAssignee?: boolean;
    compact?: boolean;
}

const TaskCard: FC<Props> = ({ task, onClick, showLocationAndAssignee = false, compact = false }) => {
    const locationNames = task.locations
        ?.map((location) => location.name)
        .filter(Boolean)
        .join(', ');

    return (
        <Card
            data={task}
            instanceId={task.instance_id}
            disabled={false}
            onClick={() => onClick(task)}
            priority={task.urgency && urgency_priority_map[task.urgency]}
            compact={compact}
        >
            <div className="flex items-center justify-between">
                <Heading4 className="font-bold">{task.title}</Heading4>
                {task.type_name && <Pill classNames={{ label: 'font-semibold' }}>{task.type_name}</Pill>}
            </div>
            <Text className="whitespace-pre-line text-sm text-slate-600">{task.details}</Text>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Calendar className="size-3 text-slate-500" />
                    <Text className="text-xs text-slate-500">{dayjs(task.meta.created_at).format('DD.MM.YYYY')}</Text>
                </div>
                <div className="flex items-center gap-2">
                    <Clock className="size-3 text-slate-500" />
                    <Text className="text-xs text-slate-500">{dayjs(task.alert_time).format('DD.MM.YYYY')}</Text>
                </div>
            </div>
            {showLocationAndAssignee && (task.employee_name || locationNames) && (
                <div className="flex items-center justify-between">
                    {task.employee_name && (
                        <div className="flex items-center gap-2">
                            <User className="size-3 text-slate-500" />
                            <Text className="text-xs text-slate-500">{task.employee_name}</Text>
                        </div>
                    )}
                    {locationNames && (
                        <div className="flex items-center gap-2">
                            <MapPin className="size-3 text-slate-500" />
                            <Text className="text-xs text-slate-500">{locationNames}</Text>
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-center justify-between">
                {task.creator_id && (
                    <Text className="text-xs text-slate-500">
                        By:{' '}
                        {task.creator
                            ? `${task.creator.name.last_name} ${task.creator.name.first_name}`
                            : task.creator_id}
                    </Text>
                )}
                {task.patient_name && <Text className="text-xs text-blue-600">{task.patient_name}</Text>}
            </div>
        </Card>
    );
};

export default TaskCard;
